/**
 * The cloud-palm art (id cloud-palm; display name from the game data), hand-made in the sample-page style (replaces the
 * C4 generator skeleton). The art's own look: soft clouds and air pressure — white cloud puffs lit from the upper right
 * with steel undersides, azure for cold, open palm prints of pressed air, upright pressure crescents, small cloud-scroll
 * spirals. No gold, no glyph circle, no ground line (that is hum-palm's pp-wave, portPalm.ts). Each move is staged
 * anticipation -> strike -> impact -> dissipation; body-moving moves leave split-body afterimages. Original designs.
 */
import { add, crescent, diamond, hash, isoRing, lerp, line, px, type G, type V } from '../kit';
import type { Scene } from '../skills';
import { C, clamp01, fade, hitStar, isoDisc, level, ringOn, since, strikeAt, tilesPx, type ArtFx } from '../families/swordKit';

// ── The art's pieces ──

const angOf = (d: V): number => Math.atan2(d[1], d[0]);

/**
 * A cloud puff sitting on a flat base at `c` (the base row's middle), `w` cells wide: 2-4 round bumps, lit from the upper
 * right (white top), a mid tone, a steel (or, `cold`, azure) underside. Drawn at dither level `lvl`.
 */
function cloud(g: G, c: V, w0: number, seed: number, lvl: number, cold = false): void {
  if (lvl <= 0 || w0 < 2) return;
  // Callers give a nominal size; drawn half again bigger (smaller read as pebbles in the game's scale).
  const w = Math.round(w0 * 1.5);
  const n = w < 7 ? 2 : w < 13 ? 3 : 4;
  const rB = w / (n + 0.6);
  const base = Math.round(c[1]);
  const left = c[0] - w / 2 + rB * 0.8;
  const step = n > 1 ? (w - rB * 1.6) / (n - 1) : 0;
  const top = base - Math.ceil(rB * 2.2) - 1;
  const hi = C('white', 8);
  const mid = cold ? C('azure', 5) : C('white', 6);
  const lo = cold ? C('azure', 3) : C('steel', 4);
  level(g, lvl);
  for (let y = top; y <= base; y++)
    for (let x = Math.floor(c[0] - w / 2 - 2); x <= Math.ceil(c[0] + w / 2 + 2); x++) {
      let best = 9;
      for (let i = 0; i < n; i++) {
        const r = rB * (0.8 + 0.45 * hash(i, seed)) * (i === (n >> 1) ? 1.25 : 1);
        const cx = left + i * step;
        const cy = base - r * 0.55;
        const dx = (x - cx) / r;
        const dy = (y - cy) / r;
        if (dx * dx + dy * dy <= 1) best = Math.min(best, dy - 0.35 * dx);
      }
      if (best > 8) continue;
      px(g, x, y, y === base ? lo : best < -0.45 ? hi : best < 0.25 ? mid : lo);
    }
  g.globalAlpha = 1;
}

/** Inside an open palm (unit space: u across, thumb on +u; v down, fingers up to about -1.1, heel at +0.9). */
function inPalm(u: number, v: number): boolean {
  const pu = u / 0.66;
  const pv = (v - 0.32) / 0.6;
  if (pu * pu + pv * pv <= 1) return true;
  const tops = [-0.82, -1.06, -1.1, -0.9];
  for (let i = 0; i < 4; i++) {
    const fx = -0.48 + i * 0.32;
    if (Math.abs(u - fx) > 0.11) continue;
    const t = tops[i]!;
    if (v >= t + 0.11 && v <= 0.2) return true;
    const dv = v - (t + 0.11);
    if (v < t + 0.11 && (u - fx) * (u - fx) + dv * dv <= 0.0121) return true;
  }
  // The thumb: a capsule from the heel out and up.
  const ax = 0.52;
  const ay = 0.5;
  const bx = 0.95;
  const by = 0.02;
  const k = clamp01(((u - ax) * (bx - ax) + (v - ay) * (by - ay)) / ((bx - ax) ** 2 + (by - ay) ** 2));
  const qx = u - (ax + (bx - ax) * k);
  const qy = v - (ay + (by - ay) * k);
  return qx * qx + qy * qy <= 0.0169;
}

/**
 * A palm print of pressed air at `c`, `s` cells half-wide, squashed by `sy` (1 upright, 0.5 lying on the ground), the
 * thumb toward `side` (+1 right, -1 left). `hollow`: the rim only (a pressure print); else a filled print, rim lit.
 */
function palmPrint(g: G, c: V, s: number, side: number, sy: number, hollow: boolean, rim: string, fill: string, lvl: number): void {
  if (lvl <= 0 || s < 2) return;
  level(g, lvl);
  const hx = Math.ceil(s * 1.05) + 1;
  const y0 = Math.floor(-s * sy * 1.25) - 1;
  const y1 = Math.ceil(s * sy * 0.95) + 1;
  const cx = Math.round(c[0]);
  const cy = Math.round(c[1]);
  const at = (x: number, y: number): boolean => inPalm((x / s) * side, y / (s * sy));
  for (let y = y0; y <= y1; y++)
    for (let x = -hx; x <= hx; x++) {
      if (!at(x, y)) continue;
      const edge = !at(x + 1, y) || !at(x - 1, y) || !at(x, y + 1) || !at(x, y - 1);
      if (hollow && !edge) continue;
      px(g, cx + x, cy + y, edge ? rim : fill);
    }
  g.globalAlpha = 1;
}

/** Upright pressure crescents bowing along `d` from `c` (`n` of them, `gap` apart), the leading one white. */
function pressure(g: G, c: V, d: V, e: number, n: number, r: number, gap: number, lvl: number): void {
  if (lvl <= 0) return;
  const a = angOf(d);
  level(g, lvl);
  for (let k = 0; k < n; k++) {
    const rr = r + k * 2;
    const p = add(c, d, k * gap + e * 3);
    const o = add(p, d, -rr);
    crescent(g, o[0], o[1], rr, a - 0.85, a + 0.85, k === 0 ? 2 : 1, k === 0 ? C('white', 8) : C('azure', 5), C('azure', 4), 1, false);
  }
  g.globalAlpha = 1;
}

/** A cloud scroll: a 1-cell spiral of `turns` turns out to radius `r` (squashed by `sy`), turned by `rot`. */
function scroll(g: G, c: V, r: number, rot: number, turns: number, head: string, body: string, sy = 1): void {
  if (r < 1) return;
  const n = Math.max(8, Math.round(r * turns * 5));
  for (let i = 0; i <= n; i++) {
    const u = i / n;
    const a = rot + u * turns * Math.PI * 2;
    px(g, c[0] + Math.cos(a) * r * u, c[1] + Math.sin(a) * r * u * sy, u > 0.7 ? head : body);
  }
}

/** Cloud puffs laid round an iso ring (rx, ry = rx / 2) at `c`; only the back half (`front` false) or the front half. */
function puffRing(g: G, c: V, rx: number, n: number, rot: number, w: number, seed: number, lvl: number, front: boolean, cold = false, lift = 0): void {
  for (let i = 0; i < n; i++) {
    const a = rot + (i / n) * Math.PI * 2;
    const sn = Math.sin(a);
    if (sn >= 0 !== front) continue;
    const ww = Math.round(w * (0.8 + hash(i, seed) * 0.4));
    cloud(g, [c[0] + Math.cos(a) * rx, c[1] + sn * rx * 0.5 - lift], ww, seed + i, lvl, cold);
  }
}

/** Dazed: three small cloud puffs orbiting over the foe's head. */
function dazed(g: G, s: Scene, e: number, life: number): void {
  if (e < 0 || e >= life) return;
  const head: V = [s.f[0], s.f[1] - s.chest * 1.85];
  const lv = fade(e, life - 6, life - 3);
  for (let i = 0; i < 3; i++) {
    const a = e * 0.55 + (i * Math.PI * 2) / 3;
    const p: V = [head[0] + Math.cos(a) * 7, head[1] + Math.sin(a) * 2.5];
    if (Math.sin(a) < 0) cloud(g, p, 3, i, Math.min(lv, 2));
    else cloud(g, p, 4, i, lv);
  }
}

// ── 1. cp-wave: a quick palm, a print of air through the foe, puffs blown out behind ──

const WAVE_HIT = 0.5;
const wave: ArtFx = {
  id: 'cloud-wave',
  skill: 'cp-wave',
  th: 'cp-wave',
  on: true,
  note: 'เกลียวเมฆเล็กหมุนเข้าฝ่ามือ -> ก้าวซัดฝ่ามือ รอยฝ่ามืออากาศทะลุเป้า คลื่นแรงดันโค้ง -> ก้อนเมฆถูกเป่าออกด้านหลังเป้า -> จาง',
  weapon: 'none',
  acts: [{ m: 'wxPalm', t0: 0.05, t1: 0.85 }],
  hits: [WAVE_HIT],
  big: WAVE_HIT,
  ghosts: [0.38, 0.44],
  stepIn: 7,
  pre: 0.3,
  hit: WAVE_HIT,
  end: 1.35,
  afterimage: 'port',
  over(g, s) {
    // Anticipation: a small cloud scroll winding into the palm.
    if (s.t >= 0.28 && s.t < WAVE_HIT) {
      const { hand } = s.limbs(s.t);
      const k = clamp01((s.t - 0.28) / 0.2);
      level(g, 3);
      scroll(g, hand, 6 - k * 3, s.t * 9, 1.2, C('white', 8), C('azure', 5));
      g.globalAlpha = 1;
    }
    const e = since(s.t, WAVE_HIT);
    if (e < 0) return;
    const c = strikeAt(s);
    const side = s.d[0] >= 0 ? 1 : -1;
    // The print: solid on contact, then a hollow rim carried on through the foe.
    if (e < 4) {
      const p = add(c, s.d, e * 4 - 5);
      palmPrint(g, p, 8 + e * 2, side, 1, e > 1, e === 0 ? C('white', 8) : C('azure', 6), e === 0 ? C('white', 7) : C('azure', 5), fade(e, 2, 3));
    }
    if (e < 6) pressure(g, add(c, s.d, 4), s.d, e, 3, 6, 4, fade(e, 3, 5));
    // Puffs blown out behind the foe.
    if (e >= 1 && e < 9)
      for (let i = 0; i < 3; i++) {
        const p = add(add(s.f, s.d, 8 + e * 2.5 + i * 3), [0, 0]);
        cloud(g, [p[0], p[1] - s.chest * (0.4 + i * 0.35)], Math.max(3, 7 - Math.floor(e / 2)), i + 2, fade(e, 4, 7));
      }
    hitStar(g, s, c, e, 'azure', 0.8);
  },
};

// ── 2. cp-seize: cloud wisps into both hands, a lunge, the cloud closes on the foe, the guard shatters, dazed ──

const SEIZE_HIT = 0.75;
const seize: ArtFx = {
  id: 'cloud-seize',
  skill: 'cp-seize',
  th: 'cp-seize',
  on: true,
  note: 'วงเหล็กเทาวาดรอบเท้า เกลียวเมฆเข้าสองมือ (ตั้งท่า) -> พุ่งเข้าเงาร่างแยก -> เมฆสองซีกงับหุบเข้าหาเป้า เศษการ์ดแตกกระเด็น -> เมฆน้อยวนเหนือหัว (มึน)',
  weapon: 'none',
  acts: [{ m: 'wxThrust', t0: 0.25, t1: 1.15 }],
  hits: [SEIZE_HIT],
  big: SEIZE_HIT,
  ghosts: [0.6, 0.66, 0.72],
  stepIn: 11,
  cast: [0.05, 0.45],
  pre: 0.55,
  hit: SEIZE_HIT,
  end: 2.2,
  afterimage: 'port',
  under(g, s) {
    // The break's tell: a steel ring drawn on round the feet over the wind-up.
    if (s.t >= 0.05 && s.t < SEIZE_HIT) {
      level(g, s.t < 0.6 ? 3 : 2);
      ringOn(g, s.a, 13, -Math.PI / 2, (s.t - 0.05) / 0.4, C('steel', 5), true);
      g.globalAlpha = 1;
    }
  },
  over(g, s) {
    // Anticipation: a scroll winding into each hand.
    if (s.t >= 0.1 && s.t < SEIZE_HIT - 0.05) {
      const { hand, handB } = s.limbs(s.t);
      const k = clamp01((s.t - 0.1) / 0.5);
      level(g, 3);
      scroll(g, hand, 7 - k * 4, s.t * 8, 1.3, C('white', 8), C('steel', 5));
      level(g, 2);
      scroll(g, handB, 6 - k * 3, -s.t * 8, 1.1, C('white', 7), C('steel', 4));
      g.globalAlpha = 1;
    }
    const e = since(s.t, SEIZE_HIT);
    if (e < 0) return;
    const c = strikeAt(s);
    // The cloud hand closing: two cloud jaws, from above and below, shutting on the foe.
    if (e < 7) {
      const gap = Math.max(3, 13 - e * 4);
      const lv = fade(e, 4, 6);
      for (const sg of [-1, 1]) {
        const y = c[1] + sg * gap;
        level(g, lv);
        crescent(g, c[0], y + sg * 6, 9, sg < 0 ? Math.PI * 1.15 : Math.PI * 0.15, sg < 0 ? Math.PI * 1.85 : Math.PI * 0.85, 3, C('white', 8), C('steel', 4), 0.6, false);
        cloud(g, [c[0] + s.d[0] * 9, y + (sg < 0 ? 1 : 3)], 6, sg + 4, lv);
      }
      g.globalAlpha = 1;
    }
    // The guard shattering: steel shards flung out and falling.
    if (e >= 1 && e < 9) {
      level(g, fade(e, 5, 7));
      for (let i = 0; i < 7; i++) {
        const a = (i / 7) * Math.PI * 2 + hash(i, 41) * 0.5;
        const v = 2.2 + hash(i, 42) * 1.6;
        const x = c[0] + Math.cos(a) * v * e;
        const y = c[1] + Math.sin(a) * v * e * 0.7 + 0.25 * e * e;
        if (i % 2) diamond(g, x, y, 1, C('steel', 5), C('white', 8));
        else line(g, [x, y], [x + Math.cos(a) * 2, y + Math.sin(a) * 2], C('steel', 6));
      }
      g.globalAlpha = 1;
    }
    hitStar(g, s, c, e, 'steel', 1.2);
    dazed(g, s, e - 4, 14);
  },
};

// ── 3. cp-storm: a cloud vortex round the caster, palm prints flung out on every side, two pressure pulses ──

const STORM_HIT = 0.55;
const STORM_HIT2 = 0.85;
const STORM_R = Math.min(60, tilesPx(2.2));
const stormVortex = (g: G, s: Scene, front: boolean): void => {
  const e = since(s.t, STORM_HIT - 0.15);
  if (e < 0 || e > 16) return;
  const rx = STORM_R * (0.3 + 0.62 * clamp01(e / 6));
  const rot = e * 0.42;
  puffRing(g, s.a, rx, 7, rot, 7 - Math.floor(e / 5), 60, fade(e, 10, 14), front, false, 3);
  // Swirl streaks chasing the puffs round (white heads, azure tails), the back half under, the front half over.
  if (e < 12) {
    level(g, fade(e, 7, 10));
    for (let i = 0; i < 3; i++) {
      const a0 = rot + (i / 3) * Math.PI * 2 + 0.35;
      for (let q = 0; q < 10; q++) {
        const a = a0 + q * 0.09;
        const sn = Math.sin(a);
        if (sn >= 0 !== front) continue;
        const r = rx * (0.92 - q * 0.012);
        px(g, s.a[0] + Math.cos(a) * r, s.a[1] + sn * r * 0.5 - 6 - (i % 2) * 5, q > 6 ? C('white', 8) : C('azure', 5));
      }
    }
    g.globalAlpha = 1;
  }
};
const storm: ArtFx = {
  id: 'cloud-storm',
  skill: 'cp-storm',
  th: 'cp-storm',
  on: true,
  note: 'วงฟ้าวาดรอบตัวบอกรัศมี -> หมุนตัว วงก้อนเมฆหมุนขยายออก รอยฝ่ามือเล็กหกทิศพุ่งออก -> คลื่นแรงดันระลอกสองที่ขอบวง -> เมฆจาง',
  weapon: 'none',
  acts: [{ m: 'wxCleave', t0: 0.05, t1: 0.95 }],
  hits: [STORM_HIT, STORM_HIT2],
  big: STORM_HIT,
  ghosts: [0.42, 0.48, 0.54],
  stepIn: 5,
  pre: 0.3,
  hit: STORM_HIT,
  end: 1.85,
  afterimage: 'port',
  under(g, s) {
    if (s.t >= 0.12 && s.t < STORM_HIT) {
      level(g, 2);
      ringOn(g, s.a, STORM_R, Math.PI, (s.t - 0.12) / 0.35, C('azure', 5), true);
      g.globalAlpha = 1;
    }
    for (const [h, k] of [[STORM_HIT, 0], [STORM_HIT2, 1]] as const) {
      const e = since(s.t, h);
      if (e < 0 || e > 7) continue;
      level(g, fade(e, 3, 5));
      isoRing(g, s.a[0], s.a[1], Math.round(STORM_R * (0.45 + 0.55 * clamp01(e / 4))), k ? C('azure', 5) : C('white', 7), k === 0);
      g.globalAlpha = 1;
    }
    stormVortex(g, s, false);
  },
  over(g, s) {
    stormVortex(g, s, true);
    const e = since(s.t, STORM_HIT);
    // Palm prints flung out on six sides.
    if (e >= 0 && e < 5)
      for (let i = 0; i < 4; i++) {
        const a = (i / 4) * Math.PI * 2 + 0.6;
        const r = STORM_R * (0.42 + 0.14 * e);
        const p: V = [s.a[0] + Math.cos(a) * r, s.a[1] + Math.sin(a) * r * 0.5 - s.chest * 0.55];
        palmPrint(g, p, 7, Math.cos(a) >= 0 ? 1 : -1, 1, e > 1, C('white', 8), C('azure', 5), fade(e, 2, 4));
      }
    // The second pulse: pressure crescents bowing out at the ring's edge.
    const e2 = since(s.t, STORM_HIT2);
    if (e2 >= 0 && e2 < 5)
      for (let i = 0; i < 8; i++) {
        const a = (i / 8) * Math.PI * 2;
        const d: V = [Math.cos(a), Math.sin(a) * 0.5];
        const p: V = [s.a[0] + d[0] * STORM_R * 0.85, s.a[1] + d[1] * STORM_R * 0.85 - 6];
        level(g, fade(e2, 2, 4));
        crescent(g, p[0] - d[0] * 5, p[1] - d[1] * 5, 5 + e2, angOf(d) - 0.7, angOf(d) + 0.7, 1, C('white', 8), C('azure', 4), 1, false);
      }
    g.globalAlpha = 1;
    hitStar(g, s, strikeAt(s), e, 'azure', 0.9);
  },
};

// ── 4. cp-breath: clouds breathed in to the chest, a slow out-breath of mist, scrolls rising (heal) ──

const BREATH_IN = 0.9;
const breath: ArtFx = {
  id: 'cloud-breath',
  skill: 'cp-breath',
  th: 'cp-breath',
  on: true,
  note: 'ก้อนเมฆรอบตัวลอยเข้าหาอก (หายใจเข้า) -> ประกายที่อก -> หายใจออกเป็นหมอกวงขยายบนพื้น เกลียวเมฆลอยขึ้นจากไหล่ ประกายฟ้าลอย -> จาง',
  weapon: 'none',
  acts: [{ m: 'wxHeal', t0: 0.1, t1: 2.4 }],
  hits: [],
  pre: 0.1,
  hit: BREATH_IN,
  end: 2.6,
  afterimage: null,
  under(g, s) {
    const e = since(s.t, BREATH_IN);
    if (e < 0 || e > 18) return;
    const rx = 9 + Math.min(e, 12) * 1.2;
    level(g, fade(e, 8, 13));
    isoRing(g, s.a[0], s.a[1], Math.round(rx), C('white', 6), e < 6);
    if (e > 3) isoRing(g, s.a[0], s.a[1], Math.round(rx * 0.6), C('azure', 5));
    g.globalAlpha = 1;
  },
  over(g, s) {
    const chest: V = [s.a[0], s.a[1] - s.chest];
    // The in-breath: six puffs drawn in to the chest, shrinking.
    if (s.t >= 0.15 && s.t < BREATH_IN) {
      const k = clamp01((s.t - 0.15) / (BREATH_IN - 0.2));
      for (let i = 0; i < 6; i++) {
        const a = (i / 6) * Math.PI * 2 + 0.4;
        const from: V = [chest[0] + Math.cos(a) * 30, chest[1] + Math.sin(a) * 16];
        const p = lerp(from, chest, k * k);
        cloud(g, p, Math.round(7 - k * 4), i, k < 0.8 ? 3 : 2);
      }
    }
    const e = since(s.t, BREATH_IN);
    if (e >= -1 && e < 3) {
      level(g, e < 1 ? 3 : 2);
      diamond(g, chest[0], chest[1], e < 1 ? 3 : 2, C('azure', 5), C('white', 8));
      g.globalAlpha = 1;
    }
    if (e < 0 || e > 20) return;
    // The out-breath: scrolls rising from both shoulders, sparkles rising round the body.
    for (const sd of [-1, 1]) {
      const k = (e % 10) / 10;
      const p: V = [s.a[0] + sd * (6 + k * 4), s.a[1] - s.chest * (1.2 + k * 0.8)];
      level(g, k < 0.6 ? 3 : 2);
      scroll(g, p, 3 + k * 2, e * 0.5 * sd, 1.1, C('white', 8), C('azure', 5));
    }
    for (let i = 0; i < 6; i++) {
      const y = s.a[1] - ((e * 2 + i * 8) % (s.chest * 1.8 + 6));
      level(g, e < 15 ? 3 : 2);
      px(g, s.a[0] + (hash(i, 77) - 0.5) * 22, y, i % 2 ? C('white', 8) : C('azure', 5));
    }
    g.globalAlpha = 1;
  },
};

// ── 5. cp-drift: a cloud cushion under the feet, dark motes shaken off (cleanse), a veil of cloud in front (counter) ──

const drift: ArtFx = {
  id: 'cloud-drift',
  skill: 'cp-drift',
  th: 'cp-drift',
  on: true,
  note: 'เบาะเมฆก่อตัวรอบเท้า ตัวเบาขึ้น -> จุดหมึกดำหลุดลอยหาย (ล้างสถานะ) -> ม่านเมฆโค้งตั้งรับอยู่ด้านหน้า เกลียวเมฆบนม่าน -> จาง',
  weapon: 'none',
  acts: [{ m: 'wxBuff', t0: 0.1, t1: 2.0 }],
  hits: [],
  pre: 0.1,
  hit: 0.9,
  end: 2.5,
  afterimage: null,
  under(g, s) {
    const e = since(s.t, 0.1);
    if (e < 0 || e > 27) return;
    const w = Math.min(7, 3 + Math.floor(e / 2));
    puffRing(g, s.a, 13, 7, e * 0.12, w, 90, fade(e, 20, 24), false);
  },
  over(g, s) {
    const e = since(s.t, 0.1);
    if (e < 0 || e > 27) return;
    const w = Math.min(7, 3 + Math.floor(e / 2));
    puffRing(g, s.a, 13, 7, e * 0.12, w, 90, fade(e, 20, 24), true);
    // Lightness: short lines rising off the cushion.
    if (e < 20) {
      level(g, 2);
      for (let i = 0; i < 4; i++) {
        const x = s.a[0] + (i - 1.5) * 5;
        const y = s.a[1] - 3 - ((e * 2 + i * 5) % 14);
        line(g, [x, y], [x, y - 2], C('white', 8));
      }
      g.globalAlpha = 1;
    }
    // Cleanse: dark motes leaving the body, rising and scattering.
    const m = since(s.t, 0.45);
    if (m >= 0 && m < 10) {
      level(g, fade(m, 5, 8));
      for (let i = 0; i < 7; i++) {
        const a = (i / 7) * Math.PI * 2;
        const x = s.a[0] + Math.cos(a) * (4 + m * 2);
        const y = s.a[1] - s.chest * (0.6 + hash(i, 3) * 0.9) - m * 1.5;
        g.fillStyle = C('ink', 2);
        g.fillRect(Math.round(x), Math.round(y), m < 5 ? 2 : 1, m < 5 ? 2 : 1);
      }
      g.globalAlpha = 1;
    }
    // The counter's veil: an upright bow of cloud in front, shimmering, three scrolls on it.
    const v = since(s.t, 0.85);
    if (v >= 0 && v < 16) {
      const a = angOf(s.d);
      const o = add([s.a[0], s.a[1] - s.chest * 0.85], s.d, -6);
      const lv = v < 2 ? 2 : v < 11 ? (v % 4 === 3 ? 2 : 3) : v < 14 ? 2 : 1;
      level(g, lv);
      crescent(g, o[0], o[1], 24, a - 0.95, a + 0.95, 3, C('white', 8), C('azure', 5), 1, false);
      for (let i = -1; i <= 1; i++) {
        const p: V = [o[0] + Math.cos(a + i * 0.6) * 22, o[1] + Math.sin(a + i * 0.6) * 22];
        scroll(g, p, 2.5, v * 0.6 + i, 1, C('white', 8), C('azure', 6));
      }
      g.globalAlpha = 1;
    }
  },
};

// ── 6. cp-roar: a cloud ball pressed in the palm, a flying palm of air, a roar of rings, mist dragging the foe's feet ──

const ROAR_REL = 0.78;
const ROAR_HIT = 1.0;
const roar: ArtFx = {
  id: 'cloud-roar',
  skill: 'cp-roar',
  th: 'cp-roar',
  on: true,
  note: 'เมฆอัดเป็นก้อนที่ฝ่ามือ -> ผลักออก รอยฝ่ามืออากาศลอยไปหาเป้า ทิ้งก้อนเมฆแตกตามหลัง (ไม่ใช่ลำแสง) -> กระแทก วงแรงดันคำรามสามชั้น -> หมอกหนักเกาะเท้าเป้า (ช้าลง) -> จาง',
  weapon: 'none',
  acts: [{ m: 'wxPalm', t0: 0.1, t1: 1.3 }],
  hits: [ROAR_HIT],
  big: ROAR_HIT,
  pre: 0.5,
  hit: ROAR_HIT,
  end: 2.1,
  afterimage: null,
  under(g, s) {
    // Mist settling on the foe's feet (the slow).
    const e = since(s.t, ROAR_HIT + 0.1);
    if (e < 0 || e > 12) return;
    level(g, fade(e, 7, 10));
    isoDisc(g, s.f, 12, C('steel', 3), C('steel', 4), fade(e, 5, 9));
    g.globalAlpha = 1;
    puffRing(g, s.f, 12, 6, 0.3, 5, 120, fade(e, 7, 10), false, true, Math.max(0, 4 - e));
  },
  over(g, s) {
    const { hand } = s.limbs(Math.min(s.t, ROAR_REL));
    const c = strikeAt(s);
    // Wind-up: scrolls converging, a cloud ball growing in the palm.
    if (s.t >= 0.35 && s.t < ROAR_REL) {
      const k = clamp01((s.t - 0.35) / (ROAR_REL - 0.4));
      level(g, 3);
      for (let i = 0; i < 2; i++) scroll(g, add(hand, [0, i ? 6 : -6], 1 - k), 4, s.t * 9 + i * 3, 1.2, C('white', 8), C('azure', 5));
      g.globalAlpha = 1;
      cloud(g, [hand[0] + s.d[0] * 2, hand[1] + 3], Math.round(3 + k * 5), 7, 3);
    }
    // The flight: a palm of pressed air travelling to the foe, puffs breaking off behind it.
    const f = since(s.t, ROAR_REL);
    const flight = Math.max(1, Math.round((ROAR_HIT - ROAR_REL) * 12));
    if (f >= 0 && f < flight) {
      const u = (f + 1) / (flight + 1);
      const p = lerp(hand, c, u);
      palmPrint(g, p, 7, s.d[0] >= 0 ? 1 : -1, 1, false, C('white', 8), C('azure', 5), 3);
      for (let i = 1; i <= 3; i++) {
        const q = lerp(hand, c, Math.max(0, u - i * 0.12));
        cloud(g, [q[0], q[1] + 4], 6 - i, i + 9, i < 3 ? 3 : 2);
      }
      pressure(g, add(p, s.d, 6), s.d, 0, 1, 7, 0, 3);
    }
    if (f >= 0 && f < 3) pressure(g, add(hand, s.d, 3), s.d, f, 2, 4, 3, fade(f, 1, 2));
    // The roar: the print spread on the foe, three rings of pressure.
    const e = since(s.t, ROAR_HIT);
    if (e < 0) return;
    if (e < 4) palmPrint(g, c, 9 + e * 2, s.d[0] >= 0 ? 1 : -1, 1, e > 0, C('white', 8), C('azure', 6), fade(e, 2, 3));
    if (e < 8)
      for (let k = 0; k < 3; k++) {
        const ee = e - k;
        if (ee < 0 || ee > 5) continue;
        level(g, fade(ee, 2, 4));
        const rx = 5 + ee * 3.5;
        const ry = 9 + ee * 4;
        const n = Math.round((rx + ry) * 2.5);
        for (let i = 0; i < n; i++) {
          const a = (i / n) * Math.PI * 2;
          if (Math.cos(a) * s.d[0] < -0.5) continue;
          px(g, c[0] + Math.cos(a) * rx, c[1] + Math.sin(a) * ry, k === 0 ? C('white', 8) : C('steel', 5));
        }
      }
    g.globalAlpha = 1;
    // The slow: mist in front of the feet.
    const m = since(s.t, ROAR_HIT + 0.1);
    if (m >= 0 && m <= 12) puffRing(g, s.f, 12, 6, 0.3, 5, 120, fade(m, 7, 10), true, true, Math.max(0, 4 - m));
    hitStar(g, s, c, e, 'azure', 1.3);
  },
};

// ── 7. cp-ring: clouds lobbed to a chosen spot, a cold cloud wall rising round it, frost glints inside ──

const RING_FLING = 0.66;
const RING_LAND = 1.15;
const RING_R = Math.min(64, tilesPx(2.5));
const ringWall = (g: G, s: Scene, front: boolean): void => {
  const e = since(s.t, RING_LAND);
  if (e < 0 || e > 18) return;
  const w = Math.min(9, 3 + e * 2);
  puffRing(g, s.f, RING_R, 12, e * 0.08, w, 140, fade(e, 13, 16), front, true, Math.min(4, e));
};
const ring: ArtFx = {
  id: 'cloud-ring',
  skill: 'cp-ring',
  th: 'cp-ring',
  on: true,
  note: 'วงฟ้าวาดบอกพื้นที่ -> ฝ่ามือส่งก้อนเมฆสามก้อนโค้งข้ามไปตกในวง -> กำแพงเมฆเย็นผุดขึ้นรอบวงหมุนช้า ประกายน้ำแข็งในวง (ช้าลง ใช้ตัวเบาไม่ได้) -> จาง',
  weapon: 'none',
  acts: [{ m: 'wxQi', t0: 0.1, t1: 1.1 }],
  hits: [RING_LAND],
  big: RING_LAND,
  pre: 0.5,
  hit: RING_LAND,
  end: 2.6,
  afterimage: null,
  under(g, s) {
    // The area's tell, drawn on before the clouds land.
    if (s.t >= 0.45 && s.t < RING_LAND) {
      level(g, 2);
      ringOn(g, s.f, RING_R, -Math.PI / 2, (s.t - 0.45) / 0.55, C('azure', 5), true);
      g.globalAlpha = 1;
    }
    const e = since(s.t, RING_LAND);
    if (e >= 0 && e < 20) {
      if (e < 8) isoDisc(g, s.f, RING_R - 2, C('azure', 3), null, 1);
      level(g, fade(e, 12, 16));
      isoRing(g, s.f[0], s.f[1], RING_R, C('azure', 6), true);
      g.globalAlpha = 1;
    }
    ringWall(g, s, false);
  },
  over(g, s) {
    const { hand } = s.limbs(Math.min(s.t, RING_FLING));
    if (s.t >= 0.4 && s.t < RING_FLING) cloud(g, [hand[0], hand[1] + 2], Math.round(3 + ((s.t - 0.4) / 0.26) * 4), 3, 3, true);
    // Three clouds lobbed over to the spot.
    const f = since(s.t, RING_FLING);
    const flight = Math.round((RING_LAND - RING_FLING) * 12);
    if (f >= 0 && f < flight)
      for (let i = 0; i < 3; i++) {
        const u = clamp01((f + 1 - i * 0.6) / flight);
        if (u <= 0) continue;
        const dst: V = [s.f[0] + (i - 1) * RING_R * 0.5, s.f[1] + (i - 1) * 4];
        const p = lerp(hand, dst, u);
        const h = Math.sin(u * Math.PI) * 34;
        cloud(g, [p[0], p[1] - h], 6, i + 20, 3, true);
        level(g, 2);
        px(g, p[0] - s.d[0] * 4, p[1] - h + 3, C('white', 8));
        g.globalAlpha = 1;
      }
    ringWall(g, s, true);
    // Frost glints twinkling inside.
    const e = since(s.t, RING_LAND);
    if (e >= 1 && e < 17)
      for (let i = 0; i < 9; i++) {
        if ((e + i) % 3 === 0) continue;
        const a = hash(i, 150) * Math.PI * 2;
        const r = Math.sqrt(hash(i, 151)) * (RING_R - 8);
        level(g, fade(e, 11, 14));
        diamond(g, s.f[0] + Math.cos(a) * r, s.f[1] + Math.sin(a) * r * 0.5 - 2 - (e % 4), (e + i) % 3 === 1 ? 1 : 0, C('azure', 5), C('white', 8));
      }
    g.globalAlpha = 1;
    hitStar(g, s, strikeAt(s), e, 'azure', 0.8);
  },
};

// ── 8. cp-heaven (ท่าใหญ่): clouds gathered to a canopy over the caster, a giant palm pressed down on the ground ──

const H_HIT = 1.3;
const H_R = Math.min(78, tilesPx(3.2));
const canopy = (s: Scene): V => [s.a[0], s.a[1] - s.chest * 2.05];
const heaven: ArtFx = {
  id: 'cloud-heaven',
  skill: 'cp-heaven',
  th: 'cp-heaven',
  on: true,
  note: 'เมฆไหลเข้ารวมเป็นหลังคาเมฆเหนือหัว วงฟ้าวาดบอกรัศมี -> หลังคาอัดตัว รอยฝ่ามือในเมฆ -> ฝ่ามือยักษ์กดลงพื้นทั้งวง คลื่นกระแทก ก้อนเมฆระเบิดออกรอบทิศ -> ศัตรูมึน เมฆโปรยลง -> จาง',
  weapon: 'none',
  acts: [
    { m: 'wxPower', t0: 0.05, t1: 1.9 },
  ],
  hits: [H_HIT],
  big: H_HIT,
  ghosts: [1.12, 1.18, 1.24],
  stepIn: 8,
  cast: [0.1, 1.05],
  pre: 1.0,
  hit: H_HIT,
  end: 2.9,
  afterimage: 'port',
  beats: [
    { t: 0.1, th: 'เมฆไหลเข้า' },
    { t: 0.9, th: 'หลังคาเมฆ' },
    { t: H_HIT, th: 'ฝ่ามือกดลง' },
    { t: 1.9, th: 'เมฆโปรย' },
  ],
  under(g, s) {
    if (s.t >= 0.3 && s.t < H_HIT) {
      level(g, 2);
      ringOn(g, s.a, H_R, -Math.PI / 2, (s.t - 0.3) / 0.8, C('azure', 5), true);
      g.globalAlpha = 1;
    }
    const e = since(s.t, H_HIT);
    if (e < 0 || e > 20) return;
    // The giant print on the ground, then shock rings to the edge.
    const side = s.d[0] >= 0 ? 1 : -1;
    const ps = H_R * 0.7;
    const pc: V = [s.a[0], s.a[1] + ps * 0.12];
    if (e < 1) palmPrint(g, pc, ps, side, 0.5, false, C('white', 8), C('azure', 4), 2);
    else if (e < 14) palmPrint(g, pc, ps, side, 0.5, true, e < 4 ? C('white', 8) : C('azure', 6), C('azure', 4), fade(e, 7, 11));
    for (let k = 0; k < 2; k++) {
      const ee = e - k * 2;
      if (ee < 0 || ee > 8) continue;
      level(g, fade(ee, 3, 6));
      isoRing(g, s.a[0], s.a[1], Math.round(H_R * (0.35 + 0.65 * clamp01(ee / 4))), k ? C('azure', 5) : C('white', 8), k === 0);
    }
    g.globalAlpha = 1;
    // Clouds blasted out along the ground (the back half here).
    if (e < 12)
      for (let i = 0; i < 12; i++) {
        const a = (i / 12) * Math.PI * 2 + 0.13;
        if (Math.sin(a) >= 0) continue;
        const r = H_R * (0.25 + 0.09 * e);
        cloud(g, [s.a[0] + Math.cos(a) * r, s.a[1] + Math.sin(a) * r * 0.5], 8 - Math.floor(e / 3), i + 200, fade(e, 6, 9));
      }
  },
  over(g, s) {
    const top = canopy(s);
    // Gathering: clouds streaming in from both sides and above to the canopy.
    if (s.t >= 0.1 && s.t < H_HIT) {
      const k = clamp01((s.t - 0.1) / 0.8);
      for (let i = 0; i < 6; i++) {
        const sd = i % 2 ? 1 : -1;
        const from: V = [top[0] + sd * (70 + i * 6), top[1] - 10 + i * 4];
        const to: V = [top[0] + sd * (6 + (i >> 1) * 12), top[1] + (i >> 1) * 2 - 2];
        const u = clamp01(k * 1.25 - i * 0.05);
        if (u <= 0) continue;
        cloud(g, lerp(from, to, u * (2 - u)), 8 + (i >> 1) * 2, i + 300, u < 0.3 ? 2 : 3);
      }
      // The canopy pressing down: a palm print appearing inside, pointing down at the ground.
      const p = since(s.t, 0.95);
      if (p >= 0) palmPrint(g, [top[0], top[1] + 4], 7 + Math.min(p, 3), s.d[0] >= 0 ? 1 : -1, 1, true, C('azure', 6), C('azure', 4), p < 2 ? 2 : 3);
    }
    const e = since(s.t, H_HIT);
    if (e < 0) return;
    // The canopy breaking up: clouds falling and thinning.
    if (e < 10)
      for (let i = 0; i < 6; i++) {
        const sd = i % 2 ? 1 : -1;
        const x = top[0] + sd * (6 + (i >> 1) * 12 + e * 3);
        const y = top[1] + (i >> 1) * 2 + e * 3;
        cloud(g, [x, y], Math.max(3, 10 - e), i + 300, fade(e, 4, 7));
      }
    // The press: pressure crescents bowing down from the canopy to the ground on the blow.
    if (e < 4) pressure(g, [top[0], top[1] + 4], [0, 1], e * 4, 3, 14, 9, fade(e, 2, 3));
    // Clouds blasted out along the ground (the front half).
    if (e < 12)
      for (let i = 0; i < 12; i++) {
        const a = (i / 12) * Math.PI * 2 + 0.13;
        if (Math.sin(a) < 0) continue;
        const r = H_R * (0.25 + 0.09 * e);
        cloud(g, [s.a[0] + Math.cos(a) * r, s.a[1] + Math.sin(a) * r * 0.5], 8 - Math.floor(e / 3), i + 200, fade(e, 6, 9));
      }
    hitStar(g, s, strikeAt(s), e, 'azure', 1.6);
    dazed(g, s, e - 3, 12);
  },
};

/** The cloud-palm moves, hand-made (each its own shape, motion and timing; one palette and motif family). */
export const CLOUDPALM_FX: readonly ArtFx[] = [wave, seize, storm, breath, drift, roar, ring, heaven];
